import { useState, useEffect, useCallback, useRef } from 'react'
import WebSocketService from '../services/WebSocketService'
import { WS_MESSAGE_TYPES } from '../constants/config'

/**
 * Hook interno para gestión del WebSocket.
 * SOLO debe usarse en DeviceStatusContext — los componentes
 * deben consumir datos via useDeviceStatus().
 *
 * Gestiona el ciclo de vida de la conexión (connect/disconnect)
 * y mantiene el estado derivado de los mensajes WS.
 */
const MAX_CONNECTION_LOG = 50

function useWebSocket() {
  const [isConnected, setIsConnected] = useState(false)
  const [devices, setDevices] = useState([])
  const [stats, setStats] = useState(null)
  const [connectionLog, setConnectionLog] = useState([])
  
  // Event subscribers for MQTT response events
  const eventSubscribers = useRef(new Map())

  const addLogEntry = useCallback((entry) => {
    // Preserve server timestamp when available so deduplication against the
    // historical fetch (admin/logs/lifecycle) works. Fallback to client time
    // only if the message arrived without one.
    setConnectionLog(prev => [
      { timestamp: entry.timestamp ?? Date.now(), ...entry },
      ...prev
    ].slice(0, MAX_CONNECTION_LOG))
  }, [])

  // Subscribe to MQTT response events (STATUS_RESPONSE, OTA_RESPONSE, RESTART_RESPONSE)
  const onDeviceEvent = useCallback((eventType, handler) => {
    if (!eventSubscribers.current.has(eventType)) {
      eventSubscribers.current.set(eventType, new Set())
    }
    eventSubscribers.current.get(eventType).add(handler)
    
    // Return unsubscribe function
    return () => {
      eventSubscribers.current.get(eventType)?.delete(handler)
    }
  }, [])

  useEffect(() => {
    // Handlers para diferentes tipos de mensajes
    const dispatchToEventSubscribers = (eventType, data) => {
      const subs = eventSubscribers.current.get(eventType)
      if (subs) subs.forEach(handler => handler({ ...data, type: eventType }))
    }

    const handleDeviceUpdate = (data) => {
      setDevices(prev => {
        const index = prev.findIndex(d => d.mac === data.mac)
        if (index >= 0) {
          const updated = [...prev]
          updated[index] = { ...updated[index], ...data.data, lastSeen: new Date() }
          return updated
        } else {
          return [...prev, { mac: data.mac, ...data.data, lastSeen: new Date() }]
        }
      })

      // Log lifecycle connect/disconnect events.
      // El status-handler emite 3 variantes que llegan envueltas en DEVICE_UPDATE:
      //   • event='lifecycle'         → IoT Core presence (data.online | data.status)
      //   • event='connectionStatus'  → firmware tp='conn' (data.status)
      //   • event='deviceStatus'      → variante legacy con data.online
      // El connectionLog acá es solo un buffer de "actividad reciente" (SystemHub lo
      // muestra crudo, Overview lo usa como trigger para refetch del endpoint canónico
      // /admin/logs/lifecycle + /admin/logs/restarts). Por eso guardamos lo mínimo.
      const ev = data.event
      const d = data.data || {}
      let lifecycleEvent = null
      if ((ev === 'lifecycle' || ev === 'deviceStatus') && d.online !== undefined) {
        lifecycleEvent = d.online ? 'connected' : 'disconnected'
      } else if ((ev === 'lifecycle' || ev === 'connectionStatus') && d.status) {
        lifecycleEvent = d.status === 'online' ? 'connected' : 'disconnected'
      }
      if (lifecycleEvent) {
        // Skip CLIENT_INITIATED_DISCONNECT — expected when a restart/OTA command
        // fires. It's not an unexpected disconnection and pollutes the log.
        const isCommandedRestart = lifecycleEvent === 'disconnected' &&
          d.disconnectReason === 'CLIENT_INITIATED_DISCONNECT'
        if (!isCommandedRestart) {
          addLogEntry({
            mac: data.mac,
            event: lifecycleEvent,
            timestamp: data.timestamp ?? Date.now(),
          })
        }
      }

      // DEVICE_UPDATE wraps status-handler broadcasts for admin subscribers.
      // Dispatch to STATUS_RESPONSE subscribers so DeviceModal gets live data.
      if (data.event === 'status') {
        dispatchToEventSubscribers(WS_MESSAGE_TYPES.STATUS_RESPONSE, data)
      }
    }

    const handleStatsUpdate = (data) => {
      setStats(data.stats)
    }

    const handleDeviceList = (data) => {
      setDevices(data.devices || [])
      if (data.stats) {
        setStats(prevStats => ({
          ...prevStats,
          uniqueClients: data.stats.uniqueClients,
          totalDevices: data.stats.totalDevices
        }))
      }
    }

    // Nota: no registramos handlers para DEVICE_CONNECTED/DISCONNECTED porque
    // el status-handler nunca emite esos types — toda lifecycle/connectionStatus
    // llega envuelta como DEVICE_UPDATE y se procesa en handleDeviceUpdate.

    // Handler genérico para MQTT response events → notificar subscribers
    const handleMqttResponseEvent = (data) => {
      const subscribers = eventSubscribers.current.get(data.type)
      if (subscribers) {
        subscribers.forEach(handler => handler(data))
      }
    }

    // Global handler: catch response-correlator messages (no type field)
    // Maps commandType → event subscriber type so DeviceModal receives them
    const COMMAND_TYPE_MAP = {
      'STATUS_REQUEST':        WS_MESSAGE_TYPES.STATUS_RESPONSE,
      'OTA_UPDATE':            WS_MESSAGE_TYPES.OTA_RESPONSE,
      'RESTART':               WS_MESSAGE_TYPES.RESTART_RESPONSE,
      'config_response':       WS_MESSAGE_TYPES.CONFIG_RESPONSE,  // GP-HIDRO SET_CONFIG, GP-SOIL SET_CONFIG
      'ph_trimv_response':     WS_MESSAGE_TYPES.CONFIG_RESPONSE,  // GP-HIDRO PH_READ_TRIMV
      'tds_raw_response':      WS_MESSAGE_TYPES.CONFIG_RESPONSE,  // GP-HIDRO TDS_READ_RAW
      'co2_cal_response':      WS_MESSAGE_TYPES.CONFIG_RESPONSE,  // GP-BOX / GP-MINI CO2_CALIBRATE
      'ota_rollback_response': WS_MESSAGE_TYPES.OTA_RESPONSE,     // base class OTA rollback
    }
    const handleGlobalMessage = (data) => {
      if (data.event === 'command_response' && data.commandType) {
        const mapped = COMMAND_TYPE_MAP[data.commandType]
        if (mapped) dispatchToEventSubscribers(mapped, data)
      }
    }

    // Registrar handlers
    WebSocketService.on(WS_MESSAGE_TYPES.DEVICE_UPDATE, handleDeviceUpdate)
    WebSocketService.on(WS_MESSAGE_TYPES.STATS_UPDATE, handleStatsUpdate)
    WebSocketService.on(WS_MESSAGE_TYPES.DEVICE_LIST, handleDeviceList)
    WebSocketService.on(WS_MESSAGE_TYPES.STATUS_RESPONSE, handleMqttResponseEvent)
    WebSocketService.on(WS_MESSAGE_TYPES.OTA_RESPONSE, handleMqttResponseEvent)
    WebSocketService.on(WS_MESSAGE_TYPES.RESTART_RESPONSE, handleMqttResponseEvent)
    WebSocketService.on('*', handleGlobalMessage)

    // Conectar (una sola vez, gestionado por el Context)
    WebSocketService.connect()
      .then(() => setIsConnected(true))
      .catch(console.error)

    // Verificar estado periódicamente
    const checkInterval = setInterval(() => {
      setIsConnected(WebSocketService.isConnected())
    }, 5000)

    // Cleanup — solo se ejecuta al desmontar DeviceStatusProvider (logout/cierre)
    return () => {
      clearInterval(checkInterval)
      WebSocketService.off(WS_MESSAGE_TYPES.DEVICE_UPDATE, handleDeviceUpdate)
      WebSocketService.off(WS_MESSAGE_TYPES.STATS_UPDATE, handleStatsUpdate)
      WebSocketService.off(WS_MESSAGE_TYPES.DEVICE_LIST, handleDeviceList)
      WebSocketService.off(WS_MESSAGE_TYPES.STATUS_RESPONSE, handleMqttResponseEvent)
      WebSocketService.off(WS_MESSAGE_TYPES.OTA_RESPONSE, handleMqttResponseEvent)
      WebSocketService.off(WS_MESSAGE_TYPES.RESTART_RESPONSE, handleMqttResponseEvent)
      WebSocketService.off('*', handleGlobalMessage)
      WebSocketService.disconnect()
    }
  }, [])

  return {
    isConnected,
    devices,
    stats,
    onDeviceEvent,
    connectionLog,
  }
}

export default useWebSocket
